
import { sprintf } from 'sprintf-js';

const SECONDS_PER_YEAR = 60 * 60 * 24 * 365;

const date_to_sec = date => {
  return Math.round(date.getTime() / 1000);
}

/**
 * @return {int} Unix Epoch time in seconds
 */
const get_now_sec = () => {
  return date_to_sec(new Date());
};

const get_now_ms = () => {
  return (new Date()).getTime();
};



// Calculate the number of days bewteen the two dates.
const days_diff = (from_date, to_date) => {
  return Math.trunc((to_date-from_date)/(1000*60*60*24));
}

// Return HH:MM:SS
const timestamp_to_time_str = (timestamp_ms) => {
  const date = new Date(timestamp_ms);
  return sprintf("%02d:%02d:%02d",
    date.getHours(), date.getMinutes(), date.getSeconds());
}

const date_to_YYYYMMDD = date => {
  return sprintf("%04d-%02d-%02d",
    date.getFullYear(), date.getMonth() + 1, date.getDate());
};

const is_last_day_of_month = date => {
  const last_day_of_month = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return date.getFullYear() === last_day_of_month.getFullYear()
    && date.getMonth() === last_day_of_month.getMonth()
    && date.getDate() === last_day_of_month.getDate();
}

/**
 * @param mmddyyyy {String} MM/DD/YYYY format
 * @returns {Date}
 */
const MMDDYYYY_to_date = (mmddyyyy) => {
  const values = mmddyyyy.split("/");
  return new Date(
    parseInt(values[2], 10),
    parseInt(values[0], 10) - 1, // Month is zero-indexed
    parseInt(values[1], 10));
}

/**
 * @param first {Date}
 * @param second {Date}
 * Return the number of days between first/second
 */
const day_diff = (first, second) => {
  return Math.round((second - first) / (1000 * 60 * 60 * 24));
}

/**
 * Returns the date with the time stripped away
 */
const today_no_time = () => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate());
}

/**
 * @param year {int}
 * @param month {int} 1-12
 */
const last_day_of_month = (year, month) => {
  return new Date(year, month, 0).getDate();
}

const timestamp_sec_to_mmddyy_hhmmss = timestamp_sec => {
  const date = new Date(timestamp_sec * 1000);
  return sprintf("%02d.%02d.%02d %02d:%02d:%02d %s",
    date.getMonth() + 1, date.getDate(), date.getFullYear() % 100,
    (date.getHours() - 1) % 12 + 1, date.getMinutes(), date.getSeconds(),
    date.getHours() > 11 ? "PM" : "AM");
}

const timestamp_str_to_mmddyy_hhmmss = (date) => {
  return sprintf("%02d.%02d.%02d %02d:%02d:%02d %s",
    date.getMonth() + 1, date.getDate(), date.getFullYear() % 100,
    (date.getHours() - 1) % 12 + 1, date.getMinutes(), date.getSeconds(),
    date.getHours() > 11 ? "PM" : "AM");
}

const elapsed_sec_to_dd_hhmmss = elapsed_sec => {
  const MIN_SEC = 60;
  const HRS_SEC = 60 * 60;
  const DAY_SEC = HRS_SEC * 24;

  const num_days = Math.floor(elapsed_sec / DAY_SEC);
  let rem = elapsed_sec % DAY_SEC;

  const num_hours = Math.floor(rem / HRS_SEC);
  rem = rem % HRS_SEC;

  const num_min = Math.floor(rem/ MIN_SEC);
  const num_sec = rem % MIN_SEC;

  if (num_days > 0) {
    return sprintf(
      "%d days %02d:%02d:%02d", num_days, num_hours, num_min, num_sec);
  }
  return sprintf(
    "%02d:%02d:%02d", num_hours, num_min, num_sec);
}

/**
 * @param dob:String MM/DD/YYYY format
 * @param reference_date:String MM/DD/YYYY
 *
 * If reference_date is defined, we calculate the age at
 * that date. Otherwise, use the current date.
 */
const get_age = (dob, reference_date) => {
  const [mm, dd, yyyy] = dob.split("/");
  const mmdd = mm + dd;

  let date;

  if (reference_date) {
    const [mm, dd, yyyy] = reference_date.split("/");
    date = new Date(
      parseInt(yyyy, 10),
      parseInt(mm, 10) + 1,
      parseInt(dd, 10));
  } else {
    date = new Date();
  }
  const curr_mm = sprintf("%02d", date.getMonth() + 1);
  const curr_dd = sprintf("%02d", date.getDate());
  const curr_mmdd = curr_mm + curr_dd;
  const curr_yy = date.getFullYear();

  let age = curr_yy - parseInt(yyyy, 10);
  if (curr_mmdd < mmdd) {
    age -= 1;
  }
  return age;
}

// https://stackoverflow.com/questions/11887934/how-to-check-if-dst-daylight-saving-time-is-in-effect-and-if-so-the-offset

/**
 * @param date: {date}
 * @return true if date is in Daylight Savings Time
 * DST is opposite in northern vs southern hemispheres
 */
const is_dst = date => {
  const jan = new Date(date.getFullYear(), 0, 1);
  const jul = new Date(date.getFullYear(), 6, 1);

  if (jan.getTimezoneOffset() === jul.getTimezoneOffset()) {
    // Current locale does not support DST
    return false;
  }

  if (date.getTimezoneOffset() === jan.getTimezoneOffset()) {
    return true;
  }

  return false;
}

const EST_OFFSET_DST = 5 * 60;
const EST_OFFSET = 4 * 60;

const utc_date_to_EST = date => {
  const current_ms = date.getTime();
  const est_offset = is_dst(date) ? EST_OFFSET_DST : EST_OFFSET;
  const est_ms = current_ms - est_offset * 60 * 1000;
  return new Date(est_ms);
}

const date_to_EST = date => {
  const current_sec = date.getTime() / 1000;
  const utc_sec = current_sec + date.getTimezoneOffset() * 60;
  const est_offset = is_dst(date) ? EST_OFFSET_DST : EST_OFFSET;
  const est_sec = utc_sec - est_offset * 60;
  return new Date(est_sec * 1000);
}

const date_to_time_str = date => {
  const hours = (date.getHours() - 1) % 12 + 1;
  return sprintf("%02d:%02d:%02d %s",
    hours, date.getMinutes(), date.getSeconds(),
    date.getHours() >= 12 ? "PM" : "AM");
}

export { get_now_sec, get_now_ms, days_diff, timestamp_to_time_str,
  is_last_day_of_month, last_day_of_month, date_to_sec,
  SECONDS_PER_YEAR, MMDDYYYY_to_date, timestamp_sec_to_mmddyy_hhmmss,
  date_to_YYYYMMDD, timestamp_str_to_mmddyy_hhmmss, elapsed_sec_to_dd_hhmmss,
  get_age, is_dst, date_to_EST, utc_date_to_EST, date_to_time_str, day_diff,
  today_no_time
};

